import type { ReactNode } from 'react';
import { useCapabilities } from '../../lib/capabilities';
import { RouteDenied } from '../ui';
import { navRegistry, settingsRegistry, resolveEntries } from '../../lib/registries';
import { moduleNavEntries, moduleSettingsEntries } from '../../lib/host';

export interface GatedRouteProps {
  /** The route's own declared path (may carry a `:param`) — looked up in the
   * same nav/settings registries that already drive the sidebar and command
   * palette (P16 §4), never a second permission/flag declared here. */
  path: string;
  children: ReactNode;
}

// Route containment (story 2-104): ModuleRoutes.tsx already renders RouteDenied
// for a denied MODULE route instead of leaking its element to a hard URL load.
// The shell's own hard-coded core routes (Properties, Inspections, Reports,
// Templates, Access console, org Features) had no equivalent — hiding the nav
// row was the only protection, so a bookmarked/typed URL still mounted the
// real page (and its data fetches) for a principal without the permission.
// This closes that gap using the SAME registry entries, not a new gate.
export function GatedRoute({ path, children }: GatedRouteProps): JSX.Element {
  const { isHydrated, hasPermission, featureState } = useCapabilities();

  // Pre-hydration the capability doc hasn't loaded (permissions/flags boot
  // empty) — stay on the real element so an entitled persona's hard load never
  // flashes a denial (mirrors ModuleRoutes.tsx's same pre-hydration rule).
  if (!isHydrated) return <>{children}</>;

  // 4-102: entries.find() in array order used to return the FIRST prefix
  // match, not the most specific one — navRegistry's unrestricted 'dashboard'
  // row (`to: '/app'`) prefix-matches every `/app/*` path, so it always won
  // before the real, specific entry (e.g. 'access', 'org-branding') further
  // down the array, silently no-op'ing every gate. An exact match on `path`
  // now always wins; failing that, the LONGEST prefix match wins — the most
  // specific registry entry, regardless of array order, so no future entry
  // addition can reintroduce this by position alone.
  const entries = [...navRegistry, ...settingsRegistry, ...moduleNavEntries(), ...moduleSettingsEntries()];
  const exact = entries.find((e) => e.to === path);
  const prefixMatch = entries
    .filter((e): e is typeof e & { to: string } => typeof e.to === 'string' && path.startsWith(`${e.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
  const entry = exact ?? prefixMatch;
  // No registry entry declares a gate for this path — nothing to enforce here
  // (e.g. /app, /app/settings, /app/activity are intentionally ungated).
  if (!entry) return <>{children}</>;

  // A missing permission is a genuine access denial. resolveEntries is then
  // the exact rule the sidebar already renders by: a locked nav row NEVER
  // links to the real page (it opens the 2-1 teaser instead), so a hard URL
  // load must land on the same teaser, not the real element — reuse the rule
  // rather than re-deriving a looser one here.
  if (entry.permission && !hasPermission(entry.permission)) return <RouteDenied reason="permission" />;
  const [resolved] = resolveEntries([entry], { hasPermission, featureState });
  if (!resolved || resolved.locked) {
    return <RouteDenied reason="locked" featureKey={entry.featureKey} name={entry.label} />;
  }
  return <>{children}</>;
}
